/**
 * Animation Constants
 * 
 * Centralized constants for consistent animation timing and transitions
 * across the application. These values ensure a cohesive visual experience
 * and make it easy to adjust animation timing globally.
 */

// Base animation durations (in seconds)
export const ANIMATION_DURATION = {
  /** Standard transition duration for most UI elements */
  STANDARD: 0.5,
  /** Longer duration for page-level transitions */
  PAGE: 0.6,
  /** Quick transitions for interactive elements */
  FAST: 0.3,
  /** Smooth transitions for hover effects */
  SMOOTH: 0.4,
} as const;

// Staggered animation delays (in seconds)
export const ANIMATION_DELAY = {
  /** Base delay unit for sequential animations */
  BASE: 0.1,
  /** Faster stagger for lists with many items */
  FAST_STAGGER: 0.05,
  /** Standard sequential delay for page sections */
  SECTION_1: 0.1,
  SECTION_2: 0.2,
  SECTION_3: 0.3,
  SECTION_4: 0.4,
  SECTION_5: 0.5,
  SECTION_6: 0.6,
} as const;

// Common animation transition objects
export const ANIMATION_TRANSITIONS = {
  /** Standard page entry animation */
  PAGE_ENTER: {
    duration: ANIMATION_DURATION.PAGE,
    delay: 0,
  },
  /** Sequential section animations for page layouts */
  SECTION_1: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_1,
  },
  SECTION_2: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_2,
  },
  SECTION_3: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_3,
  },
  SECTION_4: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_4,
  },
  SECTION_5: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_5,
  },
  SECTION_6: {
    duration: ANIMATION_DURATION.PAGE,
    delay: ANIMATION_DELAY.SECTION_6,
  },
  /** Quick transition for tab changes */
  TAB_CHANGE: {
    duration: ANIMATION_DURATION.SMOOTH,
  },
} as const;

// Helper functions for dynamic animations
export const createStaggeredDelay = (index: number, baseDelay: number = ANIMATION_DELAY.BASE): number => {
  return index * baseDelay;
};

export const createListStaggerDelay = (index: number): number => {
  return createStaggeredDelay(index, ANIMATION_DELAY.FAST_STAGGER);
};

export const createComplexStaggerDelay = (
  groupIndex: number, 
  itemIndex: number, 
  itemsPerGroup: number,
  baseDelay: number = ANIMATION_DELAY.FAST_STAGGER
): number => {
  return (groupIndex * itemsPerGroup + itemIndex) * baseDelay;
};
